import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';

// Reusable standard configuration dictionary for production-grade outlined form controls
const enterpriseNotificationSearchStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': {
            borderColor: '#CBD5E1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        },
        '&.Mui-focused': {
            borderColor: '#1976d2', // --- CORPORATE BLUE BRAND ACCENT CHANNEL ---
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& .MuiInputLabel-outlined': {
        color: '#64748B',
        fontWeight: 500,
        transform: 'translate(14px, 10px) scale(1)',
        '&.Mui-focused, &.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            color: '#1976d2',
            fontWeight: 600
        }
    },
    '& input': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    }
};

const Search = ({ visible, meta, setMeta }) => {
    const report = useSelector((state) => state.notificationdashboard.report);  
    const [formValues, setFormValues] = useState(meta)
    const [searchButtonDisabled, setSearchButtonDisabled] = useState(true)
    const [checkFormUse, setCheckFormUse] = useState("")
    
    const onInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues({
            ...formValues,
            [name]: value,
            [`page`]: 1
        });
    };

    const checkFormHasValues = () => {
        let disableButton = true
        if (formValues["trackingnumber"] != "" || formValues["name"] != "" || formValues["orderid"] != "" || formValues["email"] != ""){
            disableButton = false
        }

        return disableButton
    }

    const handleKeyUp = (event) => {
        if (event.keyCode === 13) {
            submitSearchValues();
        }
    }

    const submitSearchValues = () => {
        setMeta(prevState => ({
            ...prevState,
            ["orderid"]: formValues["orderid"],
            ["trackingnumber"]: formValues["trackingnumber"],
            ["name"]: formValues["name"],
            [`email`]: formValues["email"],
            [`page`]: 1,
        }));
        setCheckFormUse(true)
    }

    const resetFields = () => {
        setFormValues({
            ...formValues,
            ["orderid"]: "",
            ["trackingnumber"]: "",
            ["name"]: "",
            [`email`]: "",
        });
        setMeta(prevState => ({
            ...prevState,
            ["orderid"]: "",
            ["trackingnumber"]: "",
            ["name"]: "",
            [`email`]: "",
            [`page`]: 1,
        }));
    }

    const onInputClear = (name) => {
        setFormValues({
            ...formValues,
            [name]: "",
        });
        setMeta(prevState => ({
            ...prevState,
            [name]: ""
        }));
    };

    useEffect(() => {
        setCheckFormUse("") 
        setSearchButtonDisabled(checkFormHasValues())
    }, [formValues])

    useEffect(() => {
        if ((formValues["trackingnumber"] != "" || formValues["name"] != "" || formValues["orderid"] != "" || formValues["email"] != "") && checkFormUse==='') {
            setFormValues({
                ...formValues,
                ["orderid"]: "",
                ["trackingnumber"]: "",
                ["name"]: "",
                [`email`]: "",
            });
            setMeta(prevState => ({
                ...prevState,
                ["orderid"]: "",
                ["trackingnumber"]: "",
                ["name"]: "",
                [`email`]: "",
                [`page`]: 1,
            }));     
        }        
    }, [report])

    return (
        <>
            {visible &&
                <Box 
                    className="filtercontainer"
                    sx={{
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        borderRadius: '16px',
                        p: 3,
                        mb: 3,
                        width: '100%',
                        boxSizing: 'border-box'
                    }}
                >
                    <Box 
                        className="filteraccordion" 
                        onKeyUp={handleKeyUp}
                        component="form"
                        noValidate
                        autoComplete="off"
                        onSubmit={e => { e.preventDefault(); }}
                    >
                        <Grid container spacing={2.5}>
                            
                            {/* 1. Order ID Form Input Wrapper Box */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Box sx={{ minHeight: '44px' }}>
                                    <TextField
                                        value={formValues && formValues.orderid}
                                        name={"orderid"}
                                        label="Order Number"
                                        size={'small'}
                                        onChange={onInputChange}
                                        InputProps={{
                                            endAdornment: (formValues && formValues.orderid) ? (
                                                <IconButton size="small" onClick={() => onInputClear("orderid")} sx={{ color: '#94A3B8', marginRight: '-4px' }}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            ) : undefined
                                        }}
                                        variant="outlined"
                                        sx={enterpriseNotificationSearchStyles}
                                    />
                                </Box>
                            </Grid>

                            {/* 2. Parcel Tracking Number Form Input Wrapper Box */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Box sx={{ minHeight: '44px' }}>
                                    <TextField
                                        value={formValues && formValues.trackingnumber}
                                        name={"trackingnumber"}
                                        label="Tracking Number"
                                        size={'small'}
                                        onChange={onInputChange}
                                        InputProps={{
                                            endAdornment: (formValues && formValues.trackingnumber) ? (
                                                <IconButton size="small" onClick={() => onInputClear("trackingnumber")} sx={{ color: '#94A3B8', marginRight: '-4px' }}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            ) : undefined
                                        }}
                                        variant="outlined"
                                        sx={enterpriseNotificationSearchStyles}
                                    />
                                </Box>
                            </Grid>

                            {/* 3. Destination Email Target Form Input Wrapper Box */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Box sx={{ minHeight: '44px' }}>
                                    <TextField
                                        value={formValues && formValues.email}
                                        name={"email"}
                                        label="Email"
                                        size={'small'}
                                        onChange={onInputChange}
                                        InputProps={{
                                            endAdornment: (formValues && formValues.email) ? (
                                                <IconButton size="small" onClick={() => onInputClear("email")} sx={{ color: '#94A3B8', marginRight: '-4px' }}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            ) : undefined
                                        }}
                                        variant="outlined"
                                        sx={enterpriseNotificationSearchStyles}
                                    />
                                </Box>
                            </Grid>

                            {/* 4. Recipient Name Lookup Form Input Wrapper Box */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Box sx={{ minHeight: '44px' }}>
                                    <TextField
                                        value={formValues && formValues.name}
                                        name={"name"}
                                        label="Name"
                                        size={'small'}
                                        onChange={onInputChange}
                                        InputProps={{
                                            endAdornment: (formValues && formValues.name) ? (
                                                <IconButton size="small" onClick={() => onInputClear("name")} sx={{ color: '#94A3B8', marginRight: '-4px' }}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            ) : undefined
                                        }}
                                        variant="outlined"
                                        sx={enterpriseNotificationSearchStyles}
                                    />
                                </Box>
                            </Grid>

                            {/* 5. Process Confirmation Bottom Actions Controls Toolbar */}
                            <Grid item xs={12}>
                                <Box 
                                    className="filterfooter"
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'flex-end', 
                                        gap: 1.5,
                                        pt: 1,
                                        borderTop: '1px solid #E2E8F0'
                                    }}
                                >
                                    <Button 
                                        variant="outlined" 
                                        onClick={resetFields} 
                                        disabled={searchButtonDisabled}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '10px',
                                            fontWeight: 700,
                                            fontSize: '13.5px',
                                            px: 3,
                                            height: '38px',
                                            borderColor: '#E2E8F0',
                                            color: '#475569',
                                            '&:hover': {
                                                borderColor: '#CBD5E1',
                                                backgroundColor: '#F1F5F9',
                                                color: '#1E293B'
                                            },
                                            '&.Mui-disabled': {
                                                borderColor: '#E2E8F0',
                                                color: '#CBD5E1'
                                            }
                                        }}
                                    >
                                        Clear
                                    </Button>
                                    <Button 
                                        variant="contained" 
                                        onClick={submitSearchValues} 
                                        disabled={searchButtonDisabled}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '10px',
                                            fontWeight: 700,
                                            fontSize: '13.5px',
                                            px: 4,
                                            height: '38px',
                                            boxShadow: 'none',
                                            backgroundColor: '#1976d2',
                                            color: '#FFFFFF',
                                            '&:hover': { 
                                                backgroundColor: '#115293',
                                                boxShadow: 'none' 
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: '#E2E8F0',
                                                color: '#94A3B8'
                                            }
                                        }}
                                    >
                                        Search
                                    </Button>
                                </Box>
                            </Grid>

                        </Grid>
                    </Box>
                </Box>
            }
        </>
    )
}

export default Search;